import { Injectable } from '@angular/core';
import { AnalysisParams, PortfolioAnalysisParams } from '@ddt-web/data-service';
import { ErrorObject } from '@ddt-web/error-handler';
import { FundTagStoreService, FundTagUtil } from '@ddt-web/fund-tags';
import { UrlService, UrlUtils } from '@ddt-web/url';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as castArray from 'lodash/castArray';
import * as uniqBy from 'lodash/uniqBy';
import { distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';

import { WatchlistErrorCode } from '../enums';
import { AbstractWorker } from '../models/abstract-worker';
import { PasDataLoaderService } from '../pas-setup-data-workers';
import { PasPartialSecurityFailingProcessingService, PasPositionDetailsLoadedProcessingService, PasPositionDetailsPerHoldingProcessingService } from '../pas-setup-data-workers/services/post-processing';
import { PortfolioPositionsUtil } from '../utils/portfolio-positions.util';

/**
 * Looks for queryParam watchlist and populates stores with fund tags, filtering by the type of fund in the watchlist queryParam.
 */
@Injectable()
@UntilDestroy()
export class WatchlistResolverWorker extends AbstractWorker {
	constructor(
		private urlService: UrlService,
		private fundTagStoreService: FundTagStoreService,
		private pasDataLoaderService: PasDataLoaderService,
		private pasPositionDetailsPerHoldingProcessingService: PasPositionDetailsPerHoldingProcessingService,
		private pasPositionDetailsLoadedProcessingService: PasPositionDetailsLoadedProcessingService,
		private pasPartialSecurityFailingProcessingService: PasPartialSecurityFailingProcessingService
	) {
		super();
	}

	public handle(): void {
		this.urlService.queryParams$.pipe(
			filter(queryParams => !!queryParams.watchlist),
			distinctUntilChanged((previous, current) => UrlUtils.isSpecificQueryParamsEqual(previous, current, ['watchlist'])),
			switchMap(queryParams => {
				return this.fundTagStoreService.getAllFundTags()
					.pipe(
						take(1),
						map(fundTags => {
							const tagsToAnalyse: string[] = castArray(queryParams?.watchlist);
							return tagsToAnalyse.includes('all') ?
								Object.values(fundTags) :
								Object.values(fundTags).filter(fundTag => tagsToAnalyse.some(tagToAnalyse => fundTag.tags.includes(tagToAnalyse)));
						})
					);
			}),
			untilDestroyed(this)
		).subscribe(funds => {
			// globalId is later re-translated to clientId - setting to globalId as shared code is set to handle that
			const fundsToAnalyse = FundTagUtil.setGlobalIdFromClientId(funds);
			if (!fundsToAnalyse.length) {
				throw new ErrorObject({ fundsToAnalyse: [] }, WatchlistErrorCode.NO_TAGGED_FUNDS_ERROR);
			}

			const positions = PortfolioPositionsUtil.createWweParamPositions(fundsToAnalyse, '', '');
			const uniquePositions = uniqBy(positions, 'id');
			const uniqueWeight = 100 / uniquePositions.length;

			uniquePositions.forEach(position => {
				position.weight = uniqueWeight;
				position.value = undefined;
			});

			const paParams = new PortfolioAnalysisParams('transient-book', null, positions, null, 'USD');
			const analysisParams = new AnalysisParams([paParams]);

			const additionalAnalysisParams = { currency: 'USD', identifierToBeUsedAsWweApiParamId: 'transient-book' };
			this.pasDataLoaderService.populateStoresForPortfolios(
				['transient-book'],
				null,
				null,
				additionalAnalysisParams,
				null,
				null,
				null,
				null,
				{ transientAnalysisId: 'transient-book', customPortfolioBody: analysisParams }
			);
			this.pasDataLoaderService.populateStoresForPositions(
				['transient-book'],
				false,
				{ currency: 'USD' },
				{ transientAnalysisId: 'transient-book', customSecurityBody: FundTagUtil.buildSecurityParamFromTags(fundsToAnalyse) },
				[this.pasPositionDetailsLoadedProcessingService, this.pasPositionDetailsPerHoldingProcessingService, this.pasPartialSecurityFailingProcessingService]
			);
		});
	}
}
